import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';

import useExclusiveOffer from 'hooks/useExclusiveOffer';
import useModal from 'hooks/useModal';
import useUtmsFromSession from 'hooks/useUtmsFromSession';

import { sendEvent } from 'lib/gtag';
import { toOfferRedirectLink } from 'lib/links';
import { getOfferPageEventCategory } from 'lib/offer';
import placeholder from 'lib/placeholder';

const OfferWithCouponModal = dynamic(() => import('./OfferWithCouponModal'), {
  ssr: false,
});

const GoToStoreButton = ({
  gaEventCategoryPostfix = null,
  offerCoupon = null,
  offerCta = null,
  offerId,
  offerSlug,
  offerStatusName,
  offerUserVisibility,
  type = 'success',
  ...rest
}) => {
  const { utmParams } = useUtmsFromSession();
  const { showModal } = useModal();
  const { isOfferBlocked, onUnloggedUserTriesOpenExclusiveOffer } =
    useExclusiveOffer();

  const sharedProps = {
    children: offerCta ?? placeholder('actions.goToStore'),
    iconRight: <Icon name="link-blank" />,
    full: true,
    type,
  };

  const isBlocked = isOfferBlocked(offerUserVisibility, offerSlug);

  const gaEventCategory = `${getOfferPageEventCategory(offerStatusName)}${
    gaEventCategoryPostfix ? `_${gaEventCategoryPostfix}` : ''
  }`;

  if (isBlocked) {
    return (
      <Button
        onClick={() => {
          onUnloggedUserTriesOpenExclusiveOffer(offerId, offerUserVisibility);
        }}
        {...sharedProps}
        {...rest}
      />
    );
  }

  if (offerCoupon) {
    return (
      <Button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();

          sendEvent({
            action: 'modal_coupon_open',
            category: 'active_offer_buy',
            label: offerCta ?? placeholder('actions.goToStore'),
          });
          showModal(OfferWithCouponModal, {
            offerCta,
            offerCoupon,
            offerId,
            gaEventCategoryPostfix,
          });
        }}
        {...sharedProps}
        {...rest}
      />
    );
  }

  return (
    <Button
      as="a"
      href={toOfferRedirectLink(offerId, utmParams)}
      target="_blank"
      onClick={(e) => {
        e.stopPropagation();

        sendEvent({
          action: 'go_store',
          category: gaEventCategory,
          label: offerCta ?? placeholder('actions.goToStore'),
        });
      }}
      {...sharedProps}
      {...rest}
    />
  );
};

GoToStoreButton.displayName = 'GoToStoreButton';

GoToStoreButton.propTypes = {
  gaEventCategoryPostfix: PropTypes.string,
  offerCoupon: PropTypes.string,
  offerCta: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  offerSlug: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string.isRequired,
  size: PropTypes.string,
  type: PropTypes.string,
};

export default GoToStoreButton;
